import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import firebase from '../../components/library/firebase/firebase';
import { getAccountIdPrefix } from '../../components/library/helpers/local_storage';
import { simpleQueryFirestoreWithFilterOrderBy } from '../../components/utility/firestore.helper';
import actions from './actions';

const COLLECTION_NAME_CLIENT_MESSAGES = 'client_messages';

export function* sendClientMessageSaga({ payload }: any) {
  try {
    const fn = firebase.functions().httpsCallable('sendClientMessage');
    yield call(fn, {
      clientId: payload.clientId,
      area: payload.area,
      invoiceNumber: payload.invoiceNumber,
      subject: payload.subject,
      message: payload.message,
    });
    yield put(actions.sendClientMessageSuccess());
  } catch (error: any) {
    yield put(actions.sendClientMessageError(error && error.message ? error.message : String(error)));
  }
}

export function* loadClientMessagesSaga(query: any) {
  try {
    const clientId = query.payload.clientId;
    // Single-field equality filter, NO orderBy: hits the automatic single-field
    // index, so it requires no composite index. Sorting by createdAt happens
    // client-side below.
    const filters = [{ field: 'clientId', operator: '==', value: clientId }];
    const data = yield call(
      simpleQueryFirestoreWithFilterOrderBy,
      getAccountIdPrefix() + COLLECTION_NAME_CLIENT_MESSAGES,
      filters
    );
    // simpleQueryFirestoreWithFilterOrderBy returns a map keyed by doc id;
    // the doc id lives in the key (data() does not include it), so inject it.
    const messages = Object.keys(data)
      .map((key) => ({
        ...data[key],
        id: key,
      }))
      .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
    yield put(actions.loadClientMessagesSuccess(messages));
  } catch (error: any) {
    yield put(actions.loadClientMessagesError(error && error.message ? error.message : String(error)));
  }
}

// Loads every client message (across all clients) for the admin notifications
// panel. Single collection read, sorted newest-first client-side.
export function* loadAllClientMessagesSaga() {
  try {
    const snap = yield call([
      firebase.firestore().collection(getAccountIdPrefix() + COLLECTION_NAME_CLIENT_MESSAGES),
      'get',
    ]);
    const messages = snap.docs
      .map((d: any) => ({ ...d.data(), id: d.id }))
      .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
    yield put(actions.loadAllClientMessagesSuccess(messages));
  } catch (error: any) {
    yield put(actions.loadAllClientMessagesError(error && error.message ? error.message : String(error)));
  }
}

// Team replies to a client message: emails the client (via the CF) and, on
// success, reloads all messages so the reply shows in the thread.
export function* sendClientReplySaga({ payload }: any) {
  try {
    const fn = firebase.functions().httpsCallable('sendClientReply');
    yield call(fn, {
      to: payload.to,
      subject: payload.subject,
      message: payload.message,
      clientId: payload.clientId,
      clientName: payload.clientName,
    });
    yield put(actions.sendClientReplySuccess());
    yield put(actions.loadAllClientMessages());
  } catch (error: any) {
    yield put(actions.sendClientReplyError(error && error.message ? error.message : String(error)));
  }
}

export default function* clientMessagesSaga() {
  yield all([
    takeEvery(actions.SEND_CLIENT_MESSAGE, sendClientMessageSaga),
    takeLatest(actions.LOAD_CLIENT_MESSAGES, loadClientMessagesSaga),
    takeLatest(actions.LOAD_ALL_CLIENT_MESSAGES, loadAllClientMessagesSaga),
    takeEvery(actions.SEND_CLIENT_REPLY, sendClientReplySaga),
  ]);
}
